import { memo, useState, useCallback, useMemo } from "react";
import {
  IconPhone,
  IconMail,
  IconBuilding,
  IconBriefcase,
  IconCalendar,
  IconNotes,
  IconPencil,
  IconExternalLink,
  IconArrowRight,
  IconTag,
  IconCoin,
  IconUser,
  IconLayoutKanban,
  IconColumns,
  IconArrowsExchange,
} from "@tabler/icons-react";
import TransferPipelineModal from "../../../../jsx/components/ContactsBoard/components/TransferPipelineModal";
import Swal from "sweetalert2";
import MoonLoader from "react-spinners/MoonLoader";
import { useNavigate } from "react-router-dom";
import { ColorPicker, ContactLabel } from "../../../../jsx/components/ContactsBoard/Label";
import { getRandomColor } from "../../../../jsx/components/ContactsBoard/label-utils";
import "./CRMContactView.css";

const PRIORITY_CONFIG = {
  low: { label: "Baixa", color: "#dbeafe", textColor: "#1e40af" },
  medium: { label: "Média", color: "#fef3c7", textColor: "#92400e" },
  high: { label: "Alta", color: "#fecaca", textColor: "#991b1b" },
};

const TEMPERATURE_CONFIG = {
  cold: { label: "Frio", emoji: "❄️" },
  warm: { label: "Morno", emoji: "🌤️" },
  hot: { label: "Quente", emoji: "🔥" },
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const daysSince = (dateStr) => {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const CRMContactView = memo(({
  contact,
  pipelines,
  isUpdating,
  onMoveToColumn,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Pipeline atual do contato (para exibir badge do dono)
  const currentPipeline = useMemo(() => {
    if (!contact?.pipeline) return null;
    return pipelines.find((p) => p.id === contact.pipeline) || null;
  }, [pipelines, contact?.pipeline]);

  // Colunas do pipeline selecionado na edição (filtro local)
  const editColumns = useMemo(() => {
    if (!editData.pipeline) return [];
    const pipeline = pipelines.find((p) => p.id === Number(editData.pipeline));
    return pipeline?.columns || [];
  }, [pipelines, editData.pipeline]);

  // Iniciar edição — default: 1o pipeline e 1a coluna se contato não tem
  const startEdit = useCallback(() => {
    const currentPipelineId = contact?.pipeline || pipelines[0]?.id || "";
    const currentPipeline = pipelines.find((p) => p.id === Number(currentPipelineId));
    const currentColumnId = contact?.column || currentPipeline?.columns?.[0]?.id || "";

    setEditData({
      name: contact?.name || "",
      email: contact?.email || "",
      value: contact?.deal_value || contact?.value || "",
      priority: contact?.priority || "",
      temperature: contact?.temperature || "",
      observations: contact?.observations || "",
      company: contact?.company || "",
      position: contact?.position || "",
      labelName: contact?.label?.name || "",
      labelColor: contact?.label?.color || getRandomColor(),
      pipeline: String(currentPipelineId),
      column: String(currentColumnId),
    });
    setIsEditing(true);
  }, [contact, pipelines]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditData({});
  }, []);

  const handleEditChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "pipeline") {
      // Ao trocar pipeline, auto-seleciona 1a coluna
      const pipeline = pipelines.find((p) => p.id === Number(value));
      const firstCol = pipeline?.columns?.[0]?.id || "";
      setEditData((prev) => ({ ...prev, pipeline: value, column: String(firstCol) }));
    } else {
      setEditData((prev) => ({ ...prev, [name]: value }));
    }
  }, [pipelines]);

  const saveEdit = useCallback(async () => {
    try {
      const payload = {};
      if (editData.name !== contact?.name) payload.name = editData.name;
      if (editData.email !== (contact?.email || "")) payload.email = editData.email;
      if (String(editData.value) !== String(contact?.deal_value || contact?.value || "")) payload.deal_value = parseFloat(editData.value) || 0;
      if (editData.priority !== (contact?.priority || "")) payload.priority = editData.priority;
      if (editData.temperature !== (contact?.temperature || "")) payload.temperature = editData.temperature;
      if (editData.observations !== (contact?.observations || "")) payload.observations = editData.observations;
      if (editData.company !== (contact?.company || "")) payload.company = editData.company;
      if (editData.position !== (contact?.position || "")) payload.position = editData.position;

      // Pipeline/Coluna
      const newColumnId = Number(editData.column);
      const newPipelineId = Number(editData.pipeline);
      const columnChanged = newColumnId && newColumnId !== contact?.column;
      const pipelineChanged = newPipelineId && newPipelineId !== contact?.pipeline;

      if (columnChanged || pipelineChanged) {
        payload.column = newColumnId;
        if (pipelineChanged) payload.pipeline = newPipelineId;
      }

      // Label
      const currentLabelName = contact?.label?.name || "";
      const currentLabelColor = contact?.label?.color || "";
      if (editData.labelName !== currentLabelName || editData.labelColor !== currentLabelColor) {
        payload.label = editData.labelName.trim()
          ? { name: editData.labelName.trim(), color: editData.labelColor }
          : null;
      }

      if (Object.keys(payload).length === 0) {
        setIsEditing(false);
        return;
      }

      // Se mudou coluna/pipeline, usa onMoveToColumn; senão onUpdate
      if (columnChanged || pipelineChanged) {
        // Salvar campos + mover em um único update
        await onUpdate(payload);
      } else {
        await onUpdate(payload);
      }

      setIsEditing(false);
      Swal.fire({ icon: "success", title: "Atualizado", text: "Contato atualizado com sucesso", timer: 1500, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", title: "Erro", text: "Não foi possível atualizar o contato" });
    }
  }, [editData, contact, onUpdate]);

  const openInCRM = useCallback(() => {
    navigate("/crm");
  }, [navigate]);

  const ownerName = contact?.owner_name || contact?.owner?.name || "—";
  const columnMovedAt = contact?.stage_entered_at || contact?.updated_at;

  const renderDetailItem = (icon, label, value, editField) => (
    <div className="sc-crm-view__detail-item">
      <span className="sc-crm-view__detail-icon">{icon}</span>
      <div className="sc-crm-view__detail-content">
        <div className="sc-crm-view__detail-label">{label}</div>
        {isEditing && editField ? (
          editField
        ) : (
          <div className={`sc-crm-view__detail-value ${!value || value === "—" ? "sc-crm-view__detail-value--empty" : ""}`}>
            {value || "—"}
          </div>
        )}
      </div>
    </div>
  );

  const priorityConf = PRIORITY_CONFIG[contact?.priority];
  const tempConf = TEMPERATURE_CONFIG[contact?.temperature];

  return (
    <div className="sc-crm-view">
      {/* Header - Pipeline path */}
      <div className="sc-crm-view__header">
        <div className="sc-crm-view__pipeline-path">
          <span className="sc-crm-view__pipeline-name">
            {contact?.pipeline_name || "Sem pipeline"}
          </span>
          {currentPipeline?.is_owner === false && currentPipeline?.created_by_name && (
            <span className="sc-crm-view__owner-badge">
              {currentPipeline.created_by_name.split(" ")[0]}
            </span>
          )}
          <span className="sc-crm-view__pipeline-arrow">▸</span>
          <span className="sc-crm-view__column-name">
            {contact?.column_name || "Coluna"}
          </span>
        </div>
      </div>

      {/* Stats compactos */}
      <div className="sc-crm-view__stats">
        <div className="sc-crm-view__stat">
          <span className="sc-crm-view__stat-value">{formatCurrency(contact?.deal_value ?? contact?.value)}</span>
          <span className="sc-crm-view__stat-label">Valor</span>
        </div>
        <div className="sc-crm-view__stat">
          <span className="sc-crm-view__stat-value">{ownerName}</span>
          <span className="sc-crm-view__stat-label">Responsável</span>
        </div>
        <div className="sc-crm-view__stat">
          <span className="sc-crm-view__stat-value">{daysSince(columnMovedAt)}d</span>
          <span className="sc-crm-view__stat-label">No estágio</span>
        </div>
      </div>

      {/* Campos de detalhe */}
      <div className="sc-crm-view__details">
        {renderDetailItem(
          <IconUser size={16} />,
          "Nome",
          contact?.name,
          <input
            className="sc-crm-view__edit-input"
            name="name"
            value={editData.name || ""}
            onChange={handleEditChange}
            placeholder="Nome do contato"
          />
        )}

        {renderDetailItem(
          <IconPhone size={16} />,
          "Telefone",
          contact?.phone
        )}

        {renderDetailItem(
          <IconMail size={16} />,
          "E-mail",
          contact?.email,
          <input
            className="sc-crm-view__edit-input"
            name="email"
            value={editData.email || ""}
            onChange={handleEditChange}
            placeholder="email@exemplo.com"
          />
        )}

        {renderDetailItem(
          <IconBuilding size={16} />,
          "Empresa",
          contact?.company,
          <input
            className="sc-crm-view__edit-input"
            name="company"
            value={editData.company || ""}
            onChange={handleEditChange}
          />
        )}

        {renderDetailItem(
          <IconBriefcase size={16} />,
          "Cargo",
          contact?.position,
          <input
            className="sc-crm-view__edit-input"
            name="position"
            value={editData.position || ""}
            onChange={handleEditChange}
          />
        )}

        {renderDetailItem(
          <IconCoin size={16} />,
          "Valor do Negócio",
          (contact?.deal_value || contact?.value) ? formatCurrency(contact?.deal_value ?? contact?.value) : null,
          <input
            className="sc-crm-view__edit-input"
            name="value"
            type="number"
            step="0.01"
            min="0"
            value={editData.value || ""}
            onChange={handleEditChange}
            placeholder="0,00"
          />
        )}

        <div className="sc-crm-view__separator" />

        {/* Pipeline - só aparece em modo edição */}
        {renderDetailItem(
          <IconLayoutKanban size={16} />,
          "Pipeline",
          contact?.pipeline_name || "Sem pipeline",
          <select
            className="sc-crm-view__edit-select"
            name="pipeline"
            value={editData.pipeline || ""}
            onChange={handleEditChange}
          >
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}

        {/* Coluna - só aparece em modo edição */}
        {renderDetailItem(
          <IconColumns size={16} />,
          "Coluna",
          contact?.column_name || "Sem coluna",
          <select
            className="sc-crm-view__edit-select"
            name="column"
            value={editData.column || ""}
            onChange={handleEditChange}
          >
            {editColumns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        <div className="sc-crm-view__separator" />

        {/* Prioridade */}
        <div className="sc-crm-view__detail-item">
          <span className="sc-crm-view__detail-icon"><IconArrowRight size={16} /></span>
          <div className="sc-crm-view__detail-content">
            <div className="sc-crm-view__detail-label">Prioridade</div>
            {isEditing ? (
              <select
                className="sc-crm-view__edit-select"
                name="priority"
                value={editData.priority || ""}
                onChange={handleEditChange}
              >
                <option value="">Nenhuma</option>
                <option value="low">Baixa</option>
                <option value="medium">Média</option>
                <option value="high">Alta</option>
              </select>
            ) : priorityConf ? (
              <span
                className="sc-crm-view__badge"
                style={{ background: priorityConf.color, color: priorityConf.textColor }}
              >
                {priorityConf.label}
              </span>
            ) : (
              <span className="sc-crm-view__detail-value sc-crm-view__detail-value--empty">—</span>
            )}
          </div>
        </div>

        {/* Temperatura */}
        <div className="sc-crm-view__detail-item">
          <span className="sc-crm-view__detail-icon">{tempConf ? tempConf.emoji : "🌡️"}</span>
          <div className="sc-crm-view__detail-content">
            <div className="sc-crm-view__detail-label">Temperatura</div>
            {isEditing ? (
              <select
                className="sc-crm-view__edit-select"
                name="temperature"
                value={editData.temperature || ""}
                onChange={handleEditChange}
              >
                <option value="">Sem temperatura</option>
                <option value="cold">Frio</option>
                <option value="warm">Morno</option>
                <option value="hot">Quente</option>
              </select>
            ) : tempConf ? (
              <span className="sc-crm-view__detail-value">{tempConf.emoji} {tempConf.label}</span>
            ) : (
              <span className="sc-crm-view__detail-value sc-crm-view__detail-value--empty">—</span>
            )}
          </div>
        </div>

        {/* Etiqueta (Label) */}
        <div className="sc-crm-view__detail-item">
          <span className="sc-crm-view__detail-icon"><IconTag size={16} /></span>
          <div className="sc-crm-view__detail-content">
            <div className="sc-crm-view__detail-label">Etiqueta</div>
            {isEditing ? (
              <div className="sc-crm-view__label-edit">
                <input
                  className="sc-crm-view__edit-input"
                  name="labelName"
                  value={editData.labelName || ""}
                  onChange={handleEditChange}
                  placeholder="Nome da etiqueta"
                />
                {editData.labelName && (
                  <>
                    <div style={{ marginTop: 6 }}>
                      <ColorPicker selectedColor={editData.labelColor} onChange={(c) => setEditData((prev) => ({ ...prev, labelColor: c }))} />
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <ContactLabel label={{ name: editData.labelName, color: editData.labelColor }} />
                    </div>
                  </>
                )}
              </div>
            ) : contact?.label?.name ? (
              <ContactLabel label={contact.label} />
            ) : (
              <span className="sc-crm-view__detail-value sc-crm-view__detail-value--empty">—</span>
            )}
          </div>
        </div>

        <div className="sc-crm-view__separator" />

        {renderDetailItem(
          <IconCalendar size={16} />,
          "Criado em",
          formatDate(contact?.created_at)
        )}

        {renderDetailItem(
          <IconNotes size={16} />,
          "Observações",
          contact?.observations,
          <textarea
            className="sc-crm-view__edit-textarea"
            name="observations"
            value={editData.observations || ""}
            onChange={handleEditChange}
            rows={3}
          />
        )}
      </div>

      {/* Botões de edição inline */}
      {isEditing && (
        <div className="sc-crm-view__edit-actions">
          <button
            className="sc-crm-view__edit-btn sc-crm-view__edit-btn--cancel"
            onClick={cancelEdit}
          >
            Cancelar
          </button>
          <button
            className="sc-crm-view__edit-btn sc-crm-view__edit-btn--save"
            onClick={saveEdit}
            disabled={isUpdating}
          >
            {isUpdating ? <MoonLoader size={12} color="#fff" /> : "Salvar"}
          </button>
        </div>
      )}

      {/* Ações */}
      <div className="sc-crm-view__actions">
        <div className="sc-crm-view__action-row">
          <button
            className="sc-crm-view__action-btn sc-crm-view__action-btn--edit"
            onClick={isEditing ? cancelEdit : startEdit}
          >
            <IconPencil size={14} />
            {isEditing ? "Cancelar" : "Editar"}
          </button>
          <button
            className="sc-crm-view__action-btn"
            onClick={() => setShowTransferModal(true)}
            disabled={isEditing}
            title="Transferir contato para outro pipeline (registra histórico)"
            style={{
              background: "#3b82f6",
              color: "#fff",
              border: "none",
            }}
          >
            <IconArrowsExchange size={14} />
            Transferir pipeline
          </button>
          <button
            className="sc-crm-view__action-btn sc-crm-view__action-btn--open"
            onClick={openInCRM}
          >
            <IconExternalLink size={14} />
            Abrir no CRM
          </button>
        </div>
      </div>

      <TransferPipelineModal
        show={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        contact={contact}
        onSuccess={(updated) => {
          if (typeof onUpdate === "function" && updated) {
            onUpdate(updated);
          }
          Swal.fire({
            icon: "success",
            title: "Transferido",
            text: "Contato movido para o novo pipeline.",
            timer: 1500,
            showConfirmButton: false,
          });
        }}
      />
    </div>
  );
});

CRMContactView.displayName = "CRMContactView";

export default CRMContactView;
